import React, { useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import type { MapCollectionRef } from "../../api/map";
import {
  LANDMARK_CATEGORIES,
  LANDMARK_CATEGORY_LABELS,
  landmarkCategoryIcon,
} from "../../lib/landmark-icons";

// ─── Map layers sheet (ATL-106) ─────────────────────────────────────────────
// Per-collection visibility toggles off the filter bar. Collections are
// grouped the way the collections page groups them; the landmark-category
// toggles (formerly FilterBar's split-pill dropdown) fold into the same
// sheet. Hiding is display-only: hidden items still match, score and unlock.
// Multi-membership rule (applied in MapPage/shared isMapItemVisible): an item
// hides only when ALL collections it belongs to are hidden.

const GROUPS: Array<[string, string]> = [
  ["peaks", "Hill lists"],
  ["trails", "Long-distance trails"],
  ["landmarks", "Landmark collections"],
  ["regions", "Parks and regions"],
];

interface MapLayersSheetProps {
  /** Lookup from the map payload - every collection with map items. */
  collections: MapCollectionRef[];
  /** Items currently loaded per collection slug (bbox-scoped, pre-hiding). */
  counts: Map<string, number>;
  /** Hidden collection slugs. */
  hidden: Set<string>;
  onChangeHidden: (next: string[]) => void;
  /** Landmark categories currently shown. */
  landmarkCategories: string[];
  onLandmarkCategoriesChange: (categories: string[]) => void;
  landmarkCounts: Record<string, number>;
  onClose: () => void;
}

function quickBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    border: "none",
    background: "transparent",
    cursor: disabled ? "default" : "pointer",
    fontFamily: "var(--font-sans)",
    fontSize: 11.5,
    fontWeight: 600,
    padding: "2px 6px",
    borderRadius: "var(--radius-sm)",
    color: disabled ? "var(--text-faint)" : "var(--sky-400)",
  };
}

function CheckBox({ checked }: { checked: boolean }) {
  return (
    <span
      style={{
        width: 15,
        height: 15,
        flex: "0 0 auto",
        borderRadius: 4,
        display: "grid",
        placeItems: "center",
        background: checked ? "var(--accent)" : "var(--surface-sunken)",
        border: "1px solid " + (checked ? "var(--border-gold)" : "var(--border-default)"),
      }}
    >
      {checked && (
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="var(--text-on-gold)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 6 9 17l-5-5" />
        </svg>
      )}
    </span>
  );
}

const rowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  width: "100%",
  padding: "7px 10px",
  border: "none",
  borderRadius: "var(--radius-md)",
  background: "transparent",
  cursor: "pointer",
  textAlign: "left",
  font: "inherit",
  transition: "var(--t-colors)",
};

export function MapLayersSheet({
  collections,
  counts,
  hidden,
  onChangeHidden,
  landmarkCategories,
  onLandmarkCategoriesChange,
  landmarkCounts,
  onClose,
}: MapLayersSheetProps) {
  const [search, setSearch] = useState("");

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    return GROUPS.map(([type, label]) => ({
      type,
      label,
      items: collections.filter(
        (c) => c.type === type && (!q || c.name.toLowerCase().includes(q)),
      ),
    })).filter((g) => g.items.length > 0);
  }, [collections, search]);

  const toggleCollection = (slug: string) => {
    const next = new Set(hidden);
    if (next.has(slug)) next.delete(slug);
    else next.add(slug);
    onChangeHidden([...next]);
  };

  const setGroup = (slugs: string[], hide: boolean) => {
    const next = new Set(hidden);
    for (const s of slugs) {
      if (hide) next.add(s);
      else next.delete(s);
    }
    onChangeHidden([...next]);
  };

  const catSet = new Set(landmarkCategories);
  const allCatsOn = landmarkCategories.length >= LANDMARK_CATEGORIES.length;
  const q = search.trim().toLowerCase();
  const shownCategories = LANDMARK_CATEGORIES.filter(
    (cat) => !q || (LANDMARK_CATEGORY_LABELS[cat] ?? "").toLowerCase().includes(q),
  );

  return (
    <div
      style={{
        position: "absolute",
        top: "calc(100% + 8px)",
        right: 0,
        width: 296,
        maxWidth: "calc(100vw - 24px)",
        maxHeight: "min(520px, 62vh)",
        display: "flex",
        flexDirection: "column",
        borderRadius: "var(--radius-lg)",
        background: "var(--surface-overlay)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        border: "1px solid var(--border-default)",
        boxShadow: "var(--shadow-lg)",
        zIndex: 30,
      }}
    >
      {/* Header + search */}
      <div style={{ padding: "10px 12px 8px", borderBottom: "1px solid var(--border-subtle)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <span className="eyebrow" style={{ fontSize: 9.5 }}>
            Map layers
          </span>
          <button
            onClick={onClose}
            aria-label="Close map layers"
            style={{
              width: 22,
              height: 22,
              display: "grid",
              placeItems: "center",
              border: "none",
              borderRadius: "var(--radius-pill)",
              background: "var(--surface-sunken)",
              color: "var(--text-muted)",
              cursor: "pointer",
            }}
          >
            <X size={13} />
          </button>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            padding: "0 9px",
            height: 30,
            borderRadius: "var(--radius-md)",
            background: "var(--surface-sunken)",
            border: "1px solid var(--border-subtle)",
          }}
        >
          <Search size={13} color="var(--text-faint)" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search layers"
            aria-label="Search layers"
            style={{
              flex: 1,
              minWidth: 0,
              border: "none",
              outline: "none",
              background: "transparent",
              fontFamily: "var(--font-sans)",
              fontSize: 12.5,
              color: "var(--text-primary)",
            }}
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              aria-label="Clear search"
              style={{ border: "none", background: "transparent", cursor: "pointer", color: "var(--text-muted)", display: "grid", placeItems: "center", padding: 0 }}
            >
              <X size={12} />
            </button>
          )}
        </div>
      </div>

      <div style={{ overflowY: "auto", padding: 6 }}>
        {grouped.map((g) => {
          const slugs = g.items.map((c) => c.slug);
          const allShown = slugs.every((s) => !hidden.has(s));
          const noneShown = slugs.every((s) => hidden.has(s));
          return (
            <div key={g.type} style={{ marginBottom: 4 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "6px 10px 2px",
                }}
              >
                <span className="eyebrow" style={{ fontSize: 9.5 }}>
                  {g.label}
                </span>
                <div style={{ display: "flex", gap: 4 }}>
                  <button onClick={() => setGroup(slugs, false)} disabled={allShown} style={quickBtnStyle(allShown)}>
                    All
                  </button>
                  <button onClick={() => setGroup(slugs, true)} disabled={noneShown} style={quickBtnStyle(noneShown)}>
                    None
                  </button>
                </div>
              </div>
              {g.items.map((c) => {
                const checked = !hidden.has(c.slug);
                return (
                  <button
                    key={c.slug}
                    onClick={() => toggleCollection(c.slug)}
                    role="checkbox"
                    aria-checked={checked}
                    style={rowStyle}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "var(--surface-raised)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: 13,
                        fontWeight: 600,
                        color: checked ? "var(--text-primary)" : "var(--text-muted)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {c.name}
                    </span>
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--text-faint)" }}>
                      {counts.get(c.slug) ?? 0}
                    </span>
                    <CheckBox checked={checked} />
                  </button>
                );
              })}
            </div>
          );
        })}

        {/* Landmark types (moved here from the FilterBar dropdown) */}
        {shownCategories.length > 0 && (
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "6px 10px 2px",
              }}
            >
              <span className="eyebrow" style={{ fontSize: 9.5 }}>
                Landmark types
              </span>
              <div style={{ display: "flex", gap: 4 }}>
                <button
                  onClick={() => onLandmarkCategoriesChange([...LANDMARK_CATEGORIES])}
                  disabled={allCatsOn}
                  style={quickBtnStyle(allCatsOn)}
                >
                  All
                </button>
                <button
                  onClick={() => onLandmarkCategoriesChange([])}
                  disabled={landmarkCategories.length === 0}
                  style={quickBtnStyle(landmarkCategories.length === 0)}
                >
                  None
                </button>
              </div>
            </div>
            {shownCategories.map((cat) => {
              const checked = catSet.has(cat);
              return (
                <button
                  key={cat}
                  onClick={() =>
                    onLandmarkCategoriesChange(
                      checked
                        ? landmarkCategories.filter((c) => c !== cat)
                        : [...landmarkCategories, cat],
                    )
                  }
                  role="checkbox"
                  aria-checked={checked}
                  style={rowStyle}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "var(--surface-raised)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  <span style={{ display: "grid", placeItems: "center", color: checked ? "var(--gold-400)" : "var(--text-faint)" }}>
                    {landmarkCategoryIcon(cat, 14)}
                  </span>
                  <span
                    style={{
                      flex: 1,
                      fontSize: 13,
                      fontWeight: 600,
                      color: checked ? "var(--text-primary)" : "var(--text-muted)",
                    }}
                  >
                    {LANDMARK_CATEGORY_LABELS[cat]}
                  </span>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--text-faint)" }}>
                    {landmarkCounts[cat] ?? 0}
                  </span>
                  <CheckBox checked={checked} />
                </button>
              );
            })}
          </div>
        )}

        {grouped.length === 0 && shownCategories.length === 0 && (
          <div style={{ padding: "14px 10px", fontSize: 12.5, color: "var(--text-muted)" }}>
            No layers match "{search}".
          </div>
        )}
      </div>
    </div>
  );
}
